import Phaser from 'phaser';
import { PALETTE, lerpColor } from './palette';

/**
 * 遠景の浜。**常に画面上端に見えている「帰るべき場所」**。
 *
 * 実機テストで「どこまで岸に近づいているのか分かりにくい」という指摘が出た。
 * 原因は HUD の作りではなく、**画面に目的地が一度も映っていなかった**こと。
 * 上下方向は 1 m = 18 px の線形写像なので、沖 260 m 地点では浜は画面の
 * 4000 px 上にあり、残り 38 m を切るまで一度も見えない。
 * 数字とバーで距離を伝えようとしていたが、伝わるはずがなかった。
 *
 * そこで画面上端に遠景の帯を置き、進むほど帯を大きくする。
 * 厳密な透視投影ではないが、実際に遠くを見たときの見え方に近く、
 * 「まだ遠い / もうすぐ着く」が一目で分かる。
 *
 * 帯の下端は、実際の波打ち際が画面に入ってきたらそちらに合わせる。
 * これで遠景と本物の浜が二重に描かれることなく、そのまま繋がる。
 */

/** 遠景の帯の下端（px）を返す。海はこの下だけを描く。 */
export function horizonBottom(progress: number, realShoreScreenY: number): number {
  const grown = 68 + 92 * Math.max(0, Math.min(1, progress));
  return Math.max(grown, realShoreScreenY);
}

export interface HorizonOptions {
  /** 0（沖）〜 1（浜）。遠景の大きさを決める。 */
  readonly progress: number;
  /** レーン内の横位置 0〜1。わずかに視差を付ける。 */
  readonly lateral: number;
  readonly timeMs: number;
  readonly viewWidth: number;
}

/**
 * 遠景を描き、海の描画開始位置（帯の下端）を返す。
 * 空 → 山 → ホテル → ヤシ → 砂 の順に、奥から手前へ重ねる。
 */
export function drawHorizon(
  g: Phaser.GameObjects.Graphics,
  bottom: number,
  options: HorizonOptions,
): void {
  const { progress, lateral, timeMs, viewWidth: w } = options;
  if (bottom <= 0) return;

  // 近づくほど地形が大きく、砂浜が広く見える。
  const scale = 0.55 + progress * 1.05;
  const sandH = Math.min(bottom * 0.62, 16 + 78 * progress);
  const landY = bottom - sandH; // 砂と陸の境目
  // 視差。レーンの端に寄ると遠景がわずかに逆へ動く。
  const shift = (0.5 - lateral) * 26;

  // ── 空 ──
  g.fillGradientStyle(
    PALETTE.skyHigh,
    PALETTE.skyHigh,
    lerpColor(PALETTE.skyHigh, PALETTE.skyLow, 0.85),
    lerpColor(PALETTE.skyHigh, PALETTE.skyLow, 0.85),
    1,
  );
  g.fillRect(0, 0, w, landY + 1);

  // 朝の低い陽。水面のきらめきと色を合わせてある。
  const sunX = w * 0.24 + shift * 0.4;
  const sunY = landY - 8 - 14 * progress;
  g.fillStyle(PALETTE.sun, 0.5);
  g.fillCircle(sunX, sunY, 9 + 10 * progress);
  g.fillStyle(PALETTE.sun, 0.95);
  g.fillCircle(sunX, sunY, 4 + 5 * progress);

  // ── 遠い山（右にダイヤモンドヘッド） ──
  const headW = 190 * scale;
  const headH = 34 * scale;
  const headX = w * 0.78 + shift * 0.6;
  g.fillStyle(PALETTE.hillFar, 1);
  g.beginPath();
  g.moveTo(headX - headW * 0.7, landY + 1);
  g.lineTo(headX - headW * 0.26, landY - headH * 0.72);
  g.lineTo(headX + headW * 0.06, landY - headH);
  g.lineTo(headX + headW * 0.44, landY - headH * 0.38);
  g.lineTo(headX + headW * 0.75, landY + 1);
  g.closePath();
  g.fillPath();

  // 左側のなだらかな稜線。奥行きを 2 枚で出す。
  g.fillStyle(PALETTE.hill, 1);
  g.beginPath();
  g.moveTo(-20, landY + 1);
  g.lineTo(w * 0.1 + shift * 0.3, landY - headH * 0.42);
  g.lineTo(w * 0.34 + shift * 0.3, landY - headH * 0.2);
  g.lineTo(w * 0.52 + shift * 0.3, landY + 1);
  g.closePath();
  g.fillPath();

  // ── ホテル群 ──
  const buildings: readonly [number, number, number][] = [
    [0.14, 0.62, 0.052],
    [0.22, 0.95, 0.044],
    [0.29, 0.74, 0.06],
    [0.38, 1.05, 0.04],
    [0.45, 0.68, 0.05],
    [0.56, 0.88, 0.046],
  ];
  for (const [px, ph, pw] of buildings) {
    const bh = ph * 26 * scale;
    const bw = pw * w * (0.6 + scale * 0.4);
    const bx = px * w + shift * 0.8;
    g.fillStyle(PALETTE.building, 1);
    g.fillRect(bx, landY - bh, bw, bh + 1);
    // 窓の灯り。近づいたときだけ点ける（遠くでは潰れて見えない）。
    if (progress > 0.45) {
      g.fillStyle(PALETTE.buildingLit, 0.5 * (progress - 0.45) * 2);
      for (let row = 1; row * 6 < bh - 3; row++) {
        for (let col = 0; col * 6 < bw - 3; col++) {
          if ((row * 7 + col * 13 + Math.floor(px * 100)) % 3 !== 0) continue;
          g.fillRect(bx + 2 + col * 6, landY - bh + 2 + row * 6, 2.5, 3);
        }
      }
    }
  }

  // ── ヤシ ──
  const palmCount = 7;
  for (let i = 0; i < palmCount; i++) {
    const px = ((i + 0.5) / palmCount) * w + shift;
    const ph = (10 + (i % 3) * 3) * scale;
    const sway = Math.sin(timeMs / 1400 + i * 1.7) * 1.6 * scale;
    g.lineStyle(Math.max(1, 1.6 * scale), PALETTE.palm, 1);
    g.beginPath();
    g.moveTo(px, landY + 1);
    g.lineTo(px + sway, landY - ph);
    g.strokePath();
    const frond = 5.2 * scale;
    for (let f = 0; f < 5; f++) {
      const a = Math.PI + (f / 4) * Math.PI;
      g.beginPath();
      g.moveTo(px + sway, landY - ph);
      g.lineTo(
        px + sway + Math.cos(a) * frond,
        landY - ph + Math.sin(a) * frond * 0.62 - frond * 0.18,
      );
      g.strokePath();
    }
  }

  // ── 砂浜 ──
  g.fillGradientStyle(PALETTE.sandDark, PALETTE.sandDark, PALETTE.sand, PALETTE.sand, 1);
  g.fillRect(0, landY, w, sandH + 1);

  // 波打ち際の泡。帯の下端でゆっくり揺れる。
  g.lineStyle(2, PALETTE.foam, 0.5 + 0.3 * progress);
  g.beginPath();
  for (let x = 0; x <= w; x += 10) {
    const y = bottom + Math.sin(x / 38 + timeMs / 1100) * (1.5 + 2 * progress);
    if (x === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.strokePath();
}
